import type { JSX, ReactNode } from 'react'
import type { ApiFailure } from '@/api/client'
import { NETWORK_FAILURE } from '@/api/client'

/**
 * Empty, loading and error — defined once, for every screen.
 *
 * CLAUDE.md's frontend rules require all three of every component, and the
 * failure mode they are guarding against is not "a screen forgot one" but "six
 * screens each improvised one". These wrap the Atlas classes from
 * design/atlas/components.css, which is where the three states were designed
 * (S-D1), so a page never writes `.ar-skeleton` markup itself.
 */

/**
 * A skeleton that mirrors the shape of what is coming, so nothing reflows.
 *
 * `lines` rather than a fixed block: a table and a stat row are different
 * shapes, and a skeleton the wrong shape is a layout jump wearing a costume.
 */
export function Loading({
  label = 'Loading',
  lines = 3,
}: {
  label?: string
  lines?: number
}): JSX.Element {
  return (
    <div className="ar-card" aria-busy="true" aria-live="polite">
      <div className="ar-card__body ar-stack ar-stack--tight">
        <span className="ar-visually-hidden">{label}</span>
        <div className="ar-skeleton ar-skeleton--title" />
        {Array.from({ length: lines }, (_, index) => (
          <div
            key={index}
            className="ar-skeleton ar-skeleton--text"
            style={{ width: `${String(90 - index * 12)}%` }}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Never a bare "no data" — it says what would be here and how to get it
 * (design/atlas/components.css, `.ar-empty`).
 */
export function Empty({
  icon = '○',
  title,
  body,
  action,
}: {
  icon?: string
  title: string
  body: string
  action?: ReactNode
}): JSX.Element {
  return (
    <div className="ar-empty">
      <div className="ar-empty__icon" aria-hidden="true">
        {icon}
      </div>
      <p className="ar-empty__title">{title}</p>
      <p className="ar-empty__body">{body}</p>
      {action}
    </div>
  )
}

/**
 * What failed, and a way forward.
 *
 * The request id is shown only when there is one. A failure that never reached
 * the API has no envelope and no id, and printing an empty "quote this to
 * support" line would send people to support with nothing to quote.
 */
export function ErrorState({
  failure,
  title = 'That did not work',
  onRetry,
}: {
  failure: ApiFailure
  title?: string
  onRetry?: () => void
}): JSX.Element {
  return (
    <div className="ar-error-state" role="alert">
      <div className="ar-error-state__icon" aria-hidden="true">
        !
      </div>
      <p className="ar-error-state__title">
        {failure.code === NETWORK_FAILURE ? 'AugRank is unreachable' : title}
      </p>
      <p className="ar-error-state__body">{failure.message}</p>
      {failure.requestId ? (
        <p className="ar-error-state__detail">Quote this to support: {failure.requestId}</p>
      ) : null}
      {onRetry ? (
        <button type="button" className="ar-btn ar-btn--ghost ar-btn--sm" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  )
}

/**
 * A failure shown inside a form rather than in place of the screen.
 *
 * `role="alert"` and the Atlas form-summary styling, which is what S-D1
 * designed for a failed submit — the same treatment whether the message came
 * from the API or from validation at the boundary.
 */
export function InlineError({ failure }: { failure: ApiFailure | null }): JSX.Element | null {
  if (!failure) return null
  return (
    <div className="ar-form-summary" role="alert">
      <h3>{failure.message}</h3>
      {failure.requestId ? (
        <ul>
          <li>
            Request <span className="ar-mono">{failure.requestId}</span>
          </li>
        </ul>
      ) : null}
    </div>
  )
}

/** The whole viewport, while the app works out who you are. */
export function FullPageLoading({ label = 'Loading AugRank' }: { label?: string }): JSX.Element {
  return (
    <div className="app-full-state" aria-busy="true" aria-live="polite">
      <span className="ar-spinner" aria-hidden="true" />
      <p className="ar-muted">{label}</p>
    </div>
  )
}
